/**
 * The public website around the jam: landing page, account, privacy, terms and
 * credits. The jam itself lives at /host (laptop) and /j/CODE (phones); QR codes
 * go straight to /j/CODE, so phones never see this page.
 */
import { useEffect, useState, type ReactNode } from 'react';
import type { Me, ServerInfo, SongMeta } from '../shared/account.ts';
import { api, useMe, useServerInfo } from './api.ts';
import { SignIn } from './SignIn.tsx';

const UPDATED = '4 November 2026';

export function Logo({ size = 'sm' }: { size?: 'sm' | 'big' }) {
  return (
    <div className={size === 'big' ? 'logo-big' : 'logo-sm'} aria-hidden>
      <span />
      <span />
      <span />
      <span />
    </div>
  );
}

export function DonateButton({ url, children = '☕ Buy us a coffee', className = 'btn' }: { url: string; children?: ReactNode; className?: string }) {
  if (!url) return null;
  // A plain link: no third-party widget or script loads until someone clicks.
  return (
    // Through our own /go/coffee, which counts the click and sends you on to `url`.
    <a className={`${className} donate-btn`} href="/go/coffee" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

/** The guides, linked from the landing page and every footer (they're how search finds us). */
export const GUIDE_LINKS: { href: string; label: string }[] = [
  { href: '/how-to-host', label: 'How to host a jam' },
  { href: '/party-music-game', label: 'Music game for parties' },
  { href: '/group-music-activity', label: 'For classes and teams' },
  { href: '/multiplayer-beat-maker', label: 'Multiplayer beat maker' },
];

export function Footer({ info }: { info: ServerInfo | null }) {
  return (
    <footer className="site-foot">
      <nav className="foot-links foot-guides" aria-label="Guides">
        {GUIDE_LINKS.map((g) => (
          <a key={g.href} href={g.href}>
            {g.label}
          </a>
        ))}
      </nav>
      <div className="foot-links">
        <a href="/privacy">Privacy</a>
        <a href="/terms">Terms</a>
        <a href="/credits">Sound credits</a>
        <a href="/account">Account</a>
        {info?.contactEmail && <a href={`mailto:${info.contactEmail}`}>Contact</a>}
        {info?.donateUrl && <DonateButton url={info.donateUrl} className="foot-donate" />}
      </div>
      <p>No ads. No tracking. Only the host's email is stored.</p>
    </footer>
  );
}

export function Page({ children, info }: { children: ReactNode; info: ServerInfo | null }) {
  return (
    <div className="site">
      <header className="site-head">
        <a href="/" className="brand">
          <Logo /> Combinator
        </a>
        <a className="btn btn-sm" href="/host">
          Start a jam
        </a>
      </header>
      <main className="site-main">{children}</main>
      <Footer info={info} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Landing
// ---------------------------------------------------------------------------

export function Landing() {
  const info = useServerInfo();
  const [code, setCode] = useState('');
  const go = () => code.trim().length >= 4 && location.assign(`/j/${code.trim().toUpperCase()}`);
  return (
    <div className="site landing-page">
      <section className="hero">
        <Logo size="big" />
        {/* The promise is part of the heading: what search engines read as the page's topic. */}
        <h1 className="hero-title">
          Combinator
          <span className="promise">One screen plays the music. Every phone in the room becomes an instrument.</span>
        </h1>
        <p className="sub">Free, in the browser. Friends join by scanning a QR code: no app, no account.</p>
        <a className="btn btn-primary btn-xl" href="/host">
          Start a jam (free)
        </a>
        <OnAPhone />
        <form
          className="join-code"
          onSubmit={(e) => {
            e.preventDefault();
            go();
          }}
        >
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4))}
            placeholder="CODE"
            autoCapitalize="characters"
            aria-label="Room code"
          />
          <button className="btn" type="submit" disabled={code.length < 4}>
            Join with a code
          </button>
        </form>
        <HeroArt />
      </section>

      <section className="how" aria-label="How it works">
        <div className="how-step">
          <span className="how-n">1</span>
          <h3>Big screen</h3>
          <p>Open Combinator on a laptop or TV and plug it into the best speakers in the room.</p>
        </div>
        <div className="how-step">
          <span className="how-n">2</span>
          <h3>Scan</h3>
          <p>Everyone scans the QR code on the screen. Each phone becomes drums, bass, keys, a sampler or the effects.</p>
        </div>
        <div className="how-step">
          <span className="how-n">3</span>
          <h3>Play</h3>
          <p>Hits land on the beat, in key. Record the jam into a song, edit it in the piano roll and timeline, export it.</p>
        </div>
      </section>

      <section className="features">
        <div>
          <h3>🥁 Made for the room</h3>
          <p>A room full of phones plays through one sound system, locked to one clock. Nobody needs headphones.</p>
        </div>
        <div>
          <h3>🎹 A real sequencer</h3>
          <p>Patterns, piano roll, song timeline with fades, automation and motion recording, on the laptop and on every phone.</p>
        </div>
        <div>
          <h3>🎚 Mix it properly</h3>
          <p>Volume and pan for every channel and every drum voice, a 6-band EQ and a compressor with a tube mode.</p>
        </div>
        <div>
          <h3>🔊 Sounds included</h3>
          <p>Sampled drum kits, loops, instruments and wavetables, all free to use in the music you make.</p>
        </div>
      </section>

      <section className="faq">
        <h2>Questions</h2>
        <details>
          <summary>Does everyone need an account?</summary>
          <p>No. Only the person who opens a jam signs in, with a code we email them (no password). Phones join with the QR code.</p>
        </details>
        <details>
          <summary>What do I need?</summary>
          <p>A laptop (Chrome, Edge or Safari) with speakers, and phones. Wired or HDMI speakers feel tighter than Bluetooth.</p>
        </details>
        <details>
          <summary>Do the phones have to be on the same Wi-Fi?</summary>
          <p>It's best, but not required: phones on mobile data can join too. Same Wi-Fi gives the lowest delay.</p>
        </details>
        <details>
          <summary>Can I keep what we made?</summary>
          <p>Yes. Songs save to your account, you can download the song file, and "Record audio" exports the audio (the last 5 minutes are always kept, too).</p>
        </details>
        <details>
          <summary>What do you do with my data?</summary>
          <p>
            We store the host's email, saved songs and, if you ticked the box, your consent to news emails. No ads, no analytics, no selling. Details in the{' '}
            <a href="/privacy">privacy notice</a>.
          </p>
        </details>
      </section>

      <section className="guides-band">
        <h2>Guides</h2>
        <div className="guide-cards">
          <a href="/how-to-host">
            <b>How to host a jam</b>
            <span>Speakers, Wi-Fi, TV, and what to do when something's off.</span>
          </a>
          <a href="/party-music-game">
            <b>A music game for parties</b>
            <span>Everyone plays from their phone. Ideas for the night.</span>
          </a>
          <a href="/group-music-activity">
            <b>For classes and teams</b>
            <span>A 40-minute session plan where nobody needs to play an instrument.</span>
          </a>
          <a href="/multiplayer-beat-maker">
            <b>Multiplayer beat maker</b>
            <span>Patterns, piano roll, timeline and mixer, with friends on their phones.</span>
          </a>
        </div>
      </section>

      <section className="closing">
        <h2>Ready when the room is</h2>
        <a className="btn btn-primary btn-xl" href="/host">
          Start a jam (free)
        </a>
        {info?.donateUrl && (
          <p className="hint">
            Free, no ads, no paywall. If Combinator made your night, <DonateButton url={info.donateUrl} className="link-donate">a coffee keeps the server running</DonateButton>.
          </p>
        )}
      </section>
      <Footer info={info} />
    </div>
  );
}

/** Opened on a phone (often from a WhatsApp link): hosting works best on a bigger screen. */
export function OnAPhone({ he = false }: { he?: boolean }) {
  const [copied, setCopied] = useState(false);
  if (typeof window === 'undefined' || !matchMedia('(pointer: coarse)').matches) return null;
  const url = `${location.origin}/host`;
  const send = async () => {
    try {
      if (navigator.share) return await navigator.share({ title: 'Combinator', text: he ? 'לפתוח במחשב כדי להתחיל את הג׳אם' : 'Open this on the laptop to start the jam', url });
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      /* share sheet closed */
    }
  };
  return (
    <div className="on-a-phone">
      <span>{he ? 'הכי טוב לארח מהמחשב או מהטלוויזיה.' : 'Hosting works best on a laptop or TV.'}</span>
      <button className="btn btn-sm" onClick={send}>
        {copied ? (he ? 'הקישור הועתק ✓' : 'Link copied ✓') : he ? '📲 שלחו למחשב' : '📲 Send to my laptop'}
      </button>
    </div>
  );
}

/** The idea in one picture: the laptop plays and shows the QR; phones are the instruments. */
export function HeroArt() {
  const phone = (x: number, color: string, label: string, kind: 'drums' | 'keys' | 'fx', tilt: number) => (
    <g transform={`translate(${x} 150) rotate(${tilt} 45 90)`}>
      <rect width="90" height="180" rx="14" fill="#14142a" stroke={color} strokeWidth="2.5" />
      <rect x="8" y="10" width="74" height="12" rx="4" fill="#1f1f3a" />
      <text x="45" y="20" textAnchor="middle" fontSize="9" fontWeight="800" fill={color}>
        {label}
      </text>
      {kind === 'drums' &&
        Array.from({ length: 12 }, (_, i) => (
          <rect key={i} x={10 + (i % 3) * 24} y={30 + Math.floor(i / 3) * 34} width="20" height="30" rx="5" fill={`hsl(${(i * 30) % 360} 65% ${i % 5 === 0 ? 55 : 30}%)`} />
        ))}
      {kind === 'keys' &&
        Array.from({ length: 8 }, (_, i) => <rect key={i} x={10 + (i % 2) * 36} y={30 + Math.floor(i / 2) * 36} width="32" height="32" rx="5" fill={i === 3 ? color : '#2a2a4a'} />)}
      {kind === 'fx' && (
        <>
          <rect x="10" y="30" width="70" height="140" rx="8" fill="#1a1a33" />
          <circle cx="56" cy="80" r="14" fill={color} opacity="0.35" />
          <circle cx="56" cy="80" r="6" fill={color} />
        </>
      )}
    </g>
  );
  return (
    <svg className="hero-art" viewBox="0 0 640 360" role="img" aria-label="A laptop shows a QR code and plays the sound; three phones are playing drums, keys and effects">
      <defs>
        <linearGradient id="scr" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="#ff4fa3" stopOpacity="0.35" />
          <stop offset="1" stopColor="#44e0ff" stopOpacity="0.25" />
        </linearGradient>
      </defs>
      <rect x="120" y="20" width="400" height="230" rx="16" fill="#14142a" stroke="#2c2c4a" strokeWidth="3" />
      <rect x="136" y="36" width="368" height="198" rx="8" fill="url(#scr)" />
      <rect x="150" y="56" width="110" height="110" rx="10" fill="#fff" />
      {Array.from({ length: 49 }, (_, i) => ((i * 7 + 3) % 5 < 3 ? <rect key={i} x={160 + (i % 7) * 13} y={66 + Math.floor(i / 7) * 13} width="11" height="11" fill="#0b0b14" /> : null))}
      <text x="205" y="196" textAnchor="middle" fontSize="26" fontWeight="900" fill="#fff" letterSpacing="4">
        MCTP
      </text>
      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((i) => (
        <rect key={i} x={290 + i * 17} y={150 - [40, 70, 55, 90, 60, 110, 80, 50, 95, 65, 45, 75][i]} width="12" height={[40, 70, 55, 90, 60, 110, 80, 50, 95, 65, 45, 75][i]} rx="3" fill={`hsl(${300 - i * 12} 90% 62%)`} />
      ))}
      <rect x="90" y="250" width="460" height="16" rx="8" fill="#22223c" />
      {phone(30, '#ff4fa3', 'DRUMS', 'drums', -8)}
      {phone(520, '#44e0ff', 'KEYS', 'keys', 7)}
      {phone(420, '#ffd23f', 'FX', 'fx', -4)}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Account
// ---------------------------------------------------------------------------

export function Account() {
  const info = useServerInfo();
  const [me, setMe] = useMe();
  const [songs, setSongs] = useState<SongMeta[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (me) api.songs().then(setSongs, () => setSongs([]));
  }, [me]);

  if (me === undefined) return <Page info={info}>Loading…</Page>;
  if (!me)
    return (
      <Page info={info}>
        <SignIn title="Sign in" onDone={setMe} />
      </Page>
    );

  const toggle = async (on: boolean) => {
    try {
      setMe(await api.setMarketing(on));
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const remove = async () => {
    if (!confirm('Delete your account and every saved song? This can’t be undone.')) return;
    await api.deleteAccount();
    location.assign('/');
  };

  return (
    <Page info={info}>
      <h1>Your account</h1>
      <section className="card">
        <p>
          Signed in as <b>{me.email}</b> since {new Date(me.createdAt).toLocaleDateString()}.
        </p>
        <label className="check">
          <input type="checkbox" checked={me.marketing} onChange={(e) => void toggle(e.target.checked)} />
          <span>Email me about new features and sound packs (at most once a month).</span>
        </label>
        <div className="row gap wrap">
          <a className="btn btn-sm" href="/host">
            Open a jam
          </a>
          <a className="btn btn-sm" href="/api/me/export" download>
            Download my data
          </a>
          <button
            className="btn btn-sm"
            onClick={async () => {
              await api.logout();
              location.assign('/');
            }}
          >
            Sign out
          </button>
        </div>
      </section>

      <section className="card">
        <h2>Saved songs</h2>
        {songs === null ? (
          <p className="hint">Loading…</p>
        ) : songs.length === 0 ? (
          <p className="hint">Nothing saved yet. In a jam, open 💾 Songs and save.</p>
        ) : (
          <ul className="song-list">
            {songs.map((s) => (
              <li key={s.id}>
                <span>{s.name}</span>
                <small>{new Date(s.updatedAt).toLocaleString()}</small>
                <button
                  className="btn btn-xs"
                  onClick={async () => {
                    if (!confirm(`Delete “${s.name}”?`)) return;
                    await api.deleteSong(s.id);
                    setSongs(songs.filter((x) => x.id !== s.id));
                  }}
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card danger">
        <h2>Delete account</h2>
        <p className="hint">Removes your email, your saved songs and your consent records right away.</p>
        <button className="btn btn-sm danger-btn" onClick={() => void remove()}>
          Delete my account
        </button>
      </section>
      {error && <p className="error">{error}</p>}
    </Page>
  );
}

// ---------------------------------------------------------------------------
// Legal
// ---------------------------------------------------------------------------

function contactOf(info: ServerInfo | null) {
  return info?.contactEmail ? <a href={`mailto:${info.contactEmail}`}>{info.contactEmail}</a> : <span>the contact address on this site</span>;
}

export function Privacy() {
  const info = useServerInfo();
  const who = info?.operator || 'the operator of Combinator';
  return (
    <Page info={info}>
      <article className="legal">
        <h1>Privacy notice</h1>
        <p className="hint">Last updated {UPDATED}</p>
        <p>
          Combinator (combinator.club) is run by {who} ("we"). Contact: {contactOf(info)}. This notice explains what we collect, why, and your rights under the Israeli Protection of
          Privacy Law (including §11) and, where it applies, the EU GDPR.
        </p>

        <h2>What we collect</h2>
        <ul>
          <li>
            <b>If you open a jam:</b> your email address, when you created the account and last signed in, the songs you choose to save, and a session cookie that
            keeps you signed in.
          </li>
          <li>
            <b>If you tick the news box:</b> a record that you agreed, when, the exact wording you saw, and the IP address and browser it came from. We keep this
            as proof of consent, as the law requires.
          </li>
          <li>
            <b>If you join with a phone:</b> no account. The name shown on the big screen lives only in the jam's memory and disappears when the jam ends. Samples
            recorded with a phone's microphone stay in the host's browser and are not stored on our server.
          </li>
          <li>
            <b>Technical:</b> our server sees IP addresses to deliver the site and to limit abuse (for example, too many sign-in codes). They are held in memory for at
            most an hour and are not written to logs.
          </li>
          <li>
            <b>Clips:</b> if the host makes a clip of the jam, the video (the light show with the players' names, and the sound) is kept on our server for 24 hours,
            so the phones in that jam can download and share it. Then it is deleted.
          </li>
          <li>
            <b>Totals, not people:</b> we count per day how many times each page was opened, which site visitors came from (the site's name only), and how many jams,
            phones and clips there were. Nothing in these totals can identify a person: no IP addresses, cookies or device details are kept for them.
          </li>
          <li>
            <b>Language:</b> if you choose English on the Hebrew page, a cookie remembers that choice. It holds only the word "en".
          </li>
        </ul>
        <p>We don't use analytics services, advertising or tracking cookies, and we never sell or rent data.</p>

        <h2>Why</h2>
        <ul>
          <li>To run your account and save your songs (needed to provide the service you asked for).</li>
          <li>To send sign-in codes (same reason).</li>
          <li>To send occasional news and sound-pack offers, only if you opted in. Each such email says it's advertising and has a one-click unsubscribe.</li>
          <li>To keep the service secure and stop abuse.</li>
        </ul>

        <h2>Who else handles it</h2>
        <ul>
          <li>{info?.hosting || 'Our cloud provider'} hosts the server and database.</li>
          <li>Resend delivers our emails (USA, under standard contractual clauses).</li>
          <li>If you click a donate button, Buy Me a Coffee handles the payment under its own privacy policy. Nothing loads from them until you click.</li>
        </ul>

        <h2>How long</h2>
        <p>
          Account data and saved songs: until you delete them or your account. Sign-in codes: 10 minutes. Sessions: 90 days. Consent records: as long as the account
          exists. Clips: 24 hours. The daily totals hold no personal data and are kept.
        </p>

        <h2>Your rights</h2>
        <p>
          You can see and download your data, fix it, delete your account (on the <a href="/account">account page</a>, effective immediately), and withdraw marketing
          consent at any time. You can also complain to the Israeli Privacy Protection Authority, or to your local data-protection authority in the EU.
        </p>

        <h2>Age</h2>
        <p>Accounts are for people 16 and older.</p>

        <h2>Security</h2>
        <p>Everything travels over HTTPS. Sign-in codes and session tokens are stored only as one-way hashes.</p>

        <h2>Changes</h2>
        <p>If this notice changes in a way that matters, we'll say so on this page, and by email to account holders for big changes.</p>
      </article>
    </Page>
  );
}

export function Terms() {
  const info = useServerInfo();
  const who = info?.operator || 'the operator of Combinator';
  return (
    <Page info={info}>
      <article className="legal">
        <h1>Terms of use</h1>
        <p className="hint">Last updated {UPDATED}</p>
        <p>
          Combinator is a free service provided by {who}. By using it you agree to these terms. Contact: {contactOf(info)}.
        </p>
        <h2>Using Combinator</h2>
        <ul>
          <li>You must be 16 or older to open an account. Anyone can join a jam with a code from its host.</li>
          <li>Don't use Combinator to break the law, to harass people, or to attack, overload or reverse-engineer the service.</li>
          <li>Only record sounds into the sampler that you have the right to use.</li>
        </ul>
        <h2>Your music</h2>
        <p>
          What you make is yours. You let us store your saved songs only so we can give them back to you. You can use audio you record with Combinator, including the
          built-in sounds, in your own music, including commercial releases. You may not extract the built-in sounds and distribute them as samples or a sound
          library. Some built-in sounds come from other creators under their own licenses; see <a href="/credits">sound credits</a>.
        </p>
        <h2>No guarantees</h2>
        <p>
          Combinator is provided "as is", for free. We do our best to keep it running and your songs safe, but we can't promise it will always be available or error-free.
          Keep copies of anything important (Songs → Download file). To the extent the law allows, we are not liable for indirect losses or lost data.
        </p>
        <h2>Ending</h2>
        <p>You can delete your account at any time. We may suspend accounts that break these terms, or stop the service with reasonable notice.</p>
        <h2>Law</h2>
        <p>These terms are governed by the laws of the State of Israel, and the courts of Tel Aviv have jurisdiction, without taking away consumer rights you have where you live.</p>
      </article>
    </Page>
  );
}

export function Credits() {
  const info = useServerInfo();
  const [packs, setPacks] = useState<Awaited<ReturnType<typeof api.credits>> | null>(null);
  useEffect(() => {
    api.credits().then(setPacks, () => setPacks([]));
  }, []);
  const license = (l: string) =>
    ({ 'CC0-1.0': 'CC0 (public domain)', PD: 'Public domain', 'CC-BY-3.0': 'CC BY 3.0', 'CC-BY-4.0': 'CC BY 4.0', MIT: 'MIT', 'Combinator-original': 'Original Combinator sounds' })[l] ?? l;
  return (
    <Page info={info}>
      <article className="legal">
        <h1>Sound credits</h1>
        <p>Every sound built into Combinator is either made by Combinator or used under a license that allows it. Thank you to everyone who shares their sounds.</p>
        {packs === null ? (
          <p className="hint">Loading…</p>
        ) : (
          <ul className="credits">
            {packs.map((p) => (
              <li key={p.name}>
                <b>{p.name}</b> by {p.author} · {license(p.license)} · {p.files} sounds
                {p.attribution && <div className="attr">{p.attribution}</div>}
                {/^https?:/.test(p.source) && (
                  <div>
                    <a href={p.source} target="_blank" rel="noopener noreferrer">
                      Source
                    </a>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </article>
    </Page>
  );
}

/** "Signed in as … · Sign out" line, for the host start screen. */
export function SignedInAs({ me, onSignOut }: { me: Me; onSignOut: () => void }) {
  return (
    <p className="hint signed-in">
      Signed in as <b>{me.email}</b> · <a href="/account">Account</a> ·{' '}
      <button
        className="linkish"
        onClick={async () => {
          await api.logout().catch(() => {});
          onSignOut();
        }}
      >
        Sign out
      </button>
    </p>
  );
}
